import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

/** The three PX modules — the top-level navigation of the platform. */
export type ModuleKey = 'resources' | 'library' | 'projects'

/** The user's PIN PREFERENCE for the nav rail — never the rendered width. */
export type SidebarState = 'expanded' | 'collapsed'

export interface ShellState {
  /** Module the shell currently focuses; null = the home entry point. */
  activeModule: ModuleKey | null
  /**
   * Whether the nav rail is pinned open. This records INTENT only: the shell
   * derives the rendered width from this plus the viewport, so a trip below
   * the collapse breakpoint never rewrites the preference (it re-applies on
   * the way back up). Nothing is persisted — a reload is a reset.
   */
  sidebarState: SidebarState
}

const initialState: ShellState = { activeModule: null, sidebarState: 'expanded' }

/**
 * Shell slice — cross-surface UI state (which module is open, whether the nav
 * rail is pinned). Kept deliberately tiny at scaffold time; surfaces add their
 * own slices.
 */
export const shellSlice = createSlice({
  name: 'shell',
  initialState,
  reducers: {
    moduleOpened(state, action: PayloadAction<ModuleKey>) {
      state.activeModule = action.payload
    },
    homeOpened(state) {
      state.activeModule = null
    },
    setSidebarState(state, action: PayloadAction<SidebarState>) {
      state.sidebarState = action.payload
    },
  },
})

export const { moduleOpened, homeOpened, setSidebarState } = shellSlice.actions
export const selectActiveModule = (state: { shell: ShellState }) => state.shell.activeModule
export const selectSidebarState = (state: { shell: ShellState }) => state.shell.sidebarState
